import { flattenReportRows, revenueCents } from './engine';
import { commissionAmountCents } from './commission';
import { csvEscape, formatReportValue } from './format';
import { formatPeriodLabel } from './periods';
import { METRIC_BY_ID } from './metrics';
import { inferSourceType } from './revenueEvents';
import type { ReportResult } from './types';

function tFallback(key: string, labels: Record<string, string>): string {
  return labels[key] ?? key;
}

export interface ExportLabels {
  title: string;
  practitioner: string;
  service: string;
  summary: string;
  detail: string;
  metricLabels: Record<string, string>;
  columnLabels: Record<string, string>;
  detailHeaders: {
    date: string;
    practitioner: string;
    patient: string;
    service: string;
    billingMethod: string;
    clipsRedeemed: string;
    revenuePerClip: string;
    recognizedRevenue: string;
    commissionPercent: string;
    commissionAmount: string;
    sourceType: string;
  };
  pdf?: {
    brand?: string;
    clinicLabel?: string;
    generatedLabel?: string;
    periodLabel?: string;
    practitionerLabel?: string;
    allPractitioners?: string;
    clinicTotal?: string;
    keyFigures?: string;
    financialOverview?: string;
    commissionOverview?: string;
    revenueByService?: string;
    appointmentPerformance?: string;
    patientPerformance?: string;
    patientRetention?: string;
    servicePerformance?: string;
    practitionerComparison?: string;
    otherMetrics?: string;
    retentionNote?: string;
    punchCard?: string;
    invoice?: string;
    includeDetails?: string;
    performanceTitle?: string;
    practitionerOverview?: string;
    commission?: string;
    commissionUnconfigured?: string;
    detailedRecords?: string;
    serviceMix?: string;
    footerReport?: string;
    pageLabel?: string;
    pageOf?: string;
    clip?: string;
  };
}

function tableHeaders(result: ReportResult, labels: ExportLabels): string[] {
  const headers: string[] = [];
  for (const column of result.columns) {
    if (column.id === 'practitioner') headers.push(labels.practitioner);
    else if (column.id === 'service') headers.push(labels.service);
    else headers.push(tFallback(column.label, labels.metricLabels));
  }
  return headers;
}

function identityCells(
  result: ReportResult,
  row: ReturnType<typeof flattenReportRows>[number],
): string[] {
  const cells: string[] = [];
  for (const column of result.columns) {
    if (column.id === 'practitioner') {
      cells.push(row.grouping === 'service' && row.depth > 0 ? '' : row.label);
    } else if (column.id === 'service') {
      cells.push(row.grouping === 'service' || row.grouping === 'subtotal' ? row.label : '');
    }
  }
  return cells;
}

function metricCells(
  result: ReportResult,
  row: ReturnType<typeof flattenReportRows>[number],
): string[] {
  const cells: string[] = [];
  for (const column of result.columns) {
    if (column.id === 'practitioner' || column.id === 'service') continue;
    const metric = METRIC_BY_ID[column.metricId];
    cells.push(
      formatReportValue(
        row.values[column.metricId] ?? null,
        metric?.format ?? 'text',
        result.currency,
      ),
    );
  }
  return cells;
}

export function reportToCsv(result: ReportResult, labels: ExportLabels): string {
  const lines: string[] = [];
  lines.push(csvEscape(labels.title));
  lines.push(csvEscape(formatPeriodLabel(result.period)));
  lines.push('');
  lines.push(csvEscape(labels.summary));
  for (const card of result.summary) {
    const metric = METRIC_BY_ID[card.metricId];
    lines.push(
      [
        csvEscape(tFallback(metric?.labelKey ?? card.metricId, labels.metricLabels)),
        csvEscape(formatReportValue(card.value, metric?.format ?? 'text', result.currency)),
      ].join(','),
    );
  }
  lines.push('');
  const headers = tableHeaders(result, labels);
  lines.push(headers.map((value) => csvEscape(value)).join(','));

  const expanded = new Set(result.rows.map((row) => row.id));
  const visible = [...flattenReportRows(result.rows, expanded), result.totals];
  for (const row of visible) {
    const ident = identityCells(result, row);
    const metrics = metricCells(result, row);
    const combined: string[] = [];
    let identIdx = 0;
    let metricIdx = 0;
    for (const column of result.columns) {
      if (column.id === 'practitioner' || column.id === 'service') {
        combined.push(csvEscape(ident[identIdx] ?? ''));
        identIdx += 1;
      } else {
        combined.push(csvEscape(metrics[metricIdx] ?? ''));
        metricIdx += 1;
      }
    }
    lines.push(combined.join(','));
  }

  lines.push('');
  lines.push(csvEscape(labels.detail));
  lines.push(
    [
      labels.detailHeaders.sourceType,
      labels.detailHeaders.date,
      labels.detailHeaders.practitioner,
      labels.detailHeaders.patient,
      labels.detailHeaders.service,
      labels.detailHeaders.billingMethod,
      labels.detailHeaders.clipsRedeemed,
      labels.detailHeaders.revenuePerClip,
      labels.detailHeaders.recognizedRevenue,
      labels.detailHeaders.commissionPercent,
      labels.detailHeaders.commissionAmount,
    ]
      .map((value) => csvEscape(value))
      .join(','),
  );
  for (const fact of result.facts.filter((row) => row.fact_kind === 'appointment')) {
    const recognized = revenueCents(fact, result.revenueBasis);
    lines.push(
      [
        inferSourceType(fact),
        fact.starts_at,
        fact.practitioner_name ?? '',
        fact.patient_name ?? '',
        fact.service_name ?? '',
        fact.billing_method ?? '',
        fact.clips_redeemed ? String(fact.clips_redeemed) : '',
        fact.revenue_per_clip_cents == null
          ? ''
          : formatReportValue(fact.revenue_per_clip_cents, 'currency', fact.currency || result.currency),
        formatReportValue(recognized, 'currency', fact.currency || result.currency),
        fact.commission_percent == null ? '' : String(fact.commission_percent),
        formatReportValue(
          commissionAmountCents(recognized, fact.commission_percent),
          'currency',
          fact.currency || result.currency,
        ),
      ]
        .map((value) => csvEscape(value))
        .join(','),
    );
  }

  return `${lines.join('\n')}\n`;
}
